import { gql } from '@apollo/client';

export const GET_STOCK_JUMP_POSITIONS = gql`
  query GetStockJumpPositions($portfolioId: ID!) {
    stockJumpPositions(portfolioId: $portfolioId) {
      stockId
      symbol
      name
      exchange
      currency
      instrumentType
      sourceProvider
    }
  }
`;

export const SEARCH_STOCKS = gql`
  query SearchStocks($query: String!) {
    searchStocks(query: $query) {
      stockId
      symbol
      name
      exchange
      currency
      instrumentType
      sourceProvider
    }
  }
`;

export const GET_STOCK_DETAILS = gql`
  query GetStockDetails($symbol: String!, $stockId: ID, $portfolioId: ID) {
    stockDetails(symbol: $symbol, stockId: $stockId) {
      id
      symbol
      name
      instrumentType
      exchange
      mic
      isin
      currency
      dataProvider
      providerSymbol
      description
      sector
      industry
      websiteUrl
      logoUrl
      isDelisted
      metadataUpdatedAt
      marketHours {
        status
        mic
        exchangeName
        timeZone
        asOf
        validUntil
        sessions {
          opensAt
          closesAt
        }
      }
      quote {
        currency
        price
        portfolioPrice(portfolioId: $portfolioId)
        change
        changePercent
        changePercent7d
        changePercent30d
        changePercent1y
        previousClose
        dayHigh
        dayLow
        yearHigh
        yearLow
        marketCap
        volume
        sourceProvider
        dataTimestamp
      }
    }
  }
`;

export const GET_STOCK_TRANSACTIONS = gql`
  query GetStockTransactions($stockId: ID!, $portfolioId: ID!) {
    stockTransactions(stockId: $stockId, portfolioId: $portfolioId) {
      accountingWarning
      id
      transactionType
      price
      quantity
      timestamp
      fees
      feeQuantity
      cost
      proceeds
      notes
      createdAt
      updatedAt
      hidden
      currency
      brokerBaseCurrency
      brokerFXRate
      brokerBaseAmount
      fxRateToUSD
      fxSource
      locationId
      sourceLocationId
      destinationLocationId
      failedStatus
      failedAt
      realizedPnl
      costBasis
      remainingQuantity
      remainingCostBasis
      scaledValues(portfolioId: $portfolioId) {
        price
        fees
        cost
        proceeds
      }
    }
  }
`;

// Aliased to `transactions` so TransactionsPage reads it like GET_PORTFOLIO_TRANSACTIONS.
export const GET_STOCK_PORTFOLIO_TRANSACTIONS = gql`
  query GetStockPortfolioTransactions($portfolioId: ID!, $limit: Int, $beforeDateTime: String) {
    transactions: stockPortfolioTransactions(portfolioId: $portfolioId, limit: $limit, beforeDateTime: $beforeDateTime) {
      total
      items {
        transaction {
          id
          hidden
          transactionType
          price
          scaledValues(portfolioId: $portfolioId) { price cost proceeds }
          quantity
          timestamp
          fees
          feeQuantity
          cost
          proceeds
          notes
          createdAt
          updatedAt
          currency
          brokerBaseCurrency
          brokerFXRate
          brokerBaseAmount
          fxRateToUSD
          fxSource
          locationId
          sourceLocationId
          destinationLocationId
          failedStatus
          failedAt
        }
        token {
          id
          ticker
          name
          assetType
          isDelisted
        }
      }
    }
  }
`;

export const GET_STOCK_CHART = gql`
  query GetStockChart($symbol: String!, $stockId: ID!, $portfolioId: ID!) {
    stockChart(symbol: $symbol, stockId: $stockId, portfolioId: $portfolioId) {
      priceHistory {
        time
        price
      }
      transactions {
        id
        transactionType
        price
        quantity
        timestamp
        hidden
      }
      averageBuyPrice
      averageSellPrice
      holdingsAmount
    }
  }
`;

export const GET_STOCK_POSITION = gql`
  query GetStockPosition($stockId: ID!, $portfolioId: ID!) {
    stockPosition(stockId: $stockId, portfolioId: $portfolioId) {
      id
      token {
        id
        name
        ticker
        assetType
        marketData {
          currentPrice
        }
      }
      holdingsAmount
      holdingsValue
      averageBuyPrice
      averageSellPrice
      investedCost
      positionAge
      averageNetCost
      planned { basisCost proceeds pnl roi multiple }
      plannedConservative { basisCost proceeds pnl roi multiple }
      total { basisCost proceeds pnl roi multiple }
      totalConservative { basisCost proceeds pnl roi multiple }
      exitStrategy {
        levels { id amount price status warning }
        sellAmount
        proceeds
        cost
        templateId
        templateName
        isModified
        templateOutOfSync
        params {
          targetMultiple
          baseSource
          exitLevelsCount
          exitLevelsSpread
          takeInitialsEnabled
          takeInitialsMultiple
          hodlEnabled
          hodlPercentage
          advancedDistributionEnabled
          distributionCurve
          distributionFocus
          distributionIntensity
        }
      }
      invalidationLevel {
        id
        triggerPrice
        timeDurationDays
        templateId
        templateName
        isModified
        positionChanged
        originalDropPercent
        currentDropPercent
        isPriceTriggered
        isTimeTriggered
        currentPrice
        priceDropPercentRemaining
        daysHeld
        daysRemaining
      }
      current {
        basisCost
        pnl
        roi
        proceeds
        multiple
      }
      realized {
        proceeds
        pnl
        basisCost
        roi
        multiple
      }
      unrealized {
        basisCost
        pnl
        roi
        multiple
        proceeds
      }
      locationBalances {
        locationId
        name
        quantity
      }
      tags {
        id
        name
        slug
        color
      }
      transactions {
        id
        transactionType
        hidden
        accountingWarning
      }
    }
  }
`;

// Keep this operation name in sync with Header's currency-change refetch list.
export const GET_FILTERED_STOCK_POSITIONS = gql`
  query GetStockPositionsList($filter: StockPositionFilterInput!) {
    filteredStockPositions(filter: $filter) {
      totalCount
      filterOptions {
        sectors { value label }
        instrumentTypes { value label }
      }
      positions {
        id
        holdingsAmount
        holdingsValue
        investedCost
        current {
          basisCost
          proceeds
          pnl
          roi
        }
        locationBalances {
          locationId
          name
          quantity
        }
        tags {
          id
          name
          slug
          color
        }
        token {
          id
          name
          ticker
          assetType
          instrumentType
          sector
          isDelisted
          marketData {
            currentPrice
            priceChange24h
            priceChange7d
            priceChange30d
            priceChange1y
          }
        }
      }
    }
  }
`;

export const GET_STOCK_DASHBOARD = gql`
  query GetStockDashboard($portfolioId: ID!) {
    stockPortfolioSummary(portfolioId: $portfolioId) {
      holdingsValue
      valueChange24h
      valueChangePercentage24h
      current {
        basisCost
        proceeds
        pnl
        roi
      }
    }
    stockPositions(portfolioId: $portfolioId) {
      id
      holdingsAmount
      holdingsValue
      locationBalances {
        locationId
        name
        quantity
      }
      token {
        id
        instrumentType
        sector
      }
      transactions {
        id
        accountingWarning
      }
    }
  }
`;
